'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, type ReactNode } from 'react';
import { useSession } from '@/lib/auth-client';
import { HeroSectionV2, DifferentiationSection, HomeSparkSection, HostAtYourPlaceSection, DirectToRightPlace, ExploreHubsSection, ExploreThemesSection, ExploreAsksSection } from '.';
import { SizzleForSection } from '../home/SizzleForSection';
import { ConsumerFastTrackV3, FinalCTASectionV3 } from '../HomeV3';
import { SignInEmailBanner } from '../auth/SignInEmailBanner';
import { DirectToRightPlaceV4 } from './DirectToRightPlaceV4';

/**
 * Alternating background palette for visual contrast between sections.
 * The system cycles through these for content sections, ensuring
 * adjacent sections always look distinct.
 */
const SECTION_BACKGROUNDS = ['bg-white', 'bg-gray-100'] as const;
interface HomeSection {
  key: string;
  /** The rendered content. */
  content: ReactNode;
  /**
   * When true, this section is a SignInEmailBanner that hides itself when
   * the user is logged in. The alternation system excludes hidden banners
   * so the light/dark rhythm stays correct.
   */
  isSignInBanner?: boolean;
  /**
   * Override the automatic alternating background. Use for sections with
   * their own gradient/dark styling (hero, dark CTAs, etc.).
   */
  bgOverride?: string;
}

/**
 * Master section order. Reorder, add, or remove entries here to control
 * the homepage layout. The alternating background is computed automatically.
 */
const SECTIONS: HomeSection[] = [
// Hero has its own gradient overlay
{
  key: 'hero',
  content: <HeroSectionV2 />,
  bgOverride: 'bg-white'
},
// ConsumerFastTrack has its own warm gradient
// { key: 'fast-track', content: <ConsumerFastTrackV3 />, bgOverride: 'bg-white' },
// { key: 'sizzle-for', content: <SizzleForSection /> },
{
  key: 'banner-early-preview',
  isSignInBanner: true,
  content: <SignInEmailBanner heading="Join Sizzle as an Early Insider" subtext="Discover products worth trying, or get yours in front of the right people. One platform for consumers, brands, and trade." buttonLabel="Get Early preview" className="py-10" />
},
// DirectToRightPlace has its own dark gradient
{
  key: 'direct-to-right-place',
  content: <DirectToRightPlaceV4 />,
  bgOverride: 'bg-gray-900'
}, {
  key: 'activation-examples',
  content: <HomeSparkSection />
},
// { key: 'differentiation', content: <DifferentiationSection /> },
{
  key: 'banner-get-started',
  isSignInBanner: true,
  content: <SignInEmailBanner heading="Ready to get started?" subtext="Whether you're here to discover great products or grow your brand, sign up and start making things happen." buttonLabel="Sign Up" className="py-10" />
}, {
  key: 'explore-hubs',
  content: <ExploreHubsSection />
}, {
  key: 'explore-themes',
  content: <ExploreThemesSection />
}, {
  key: 'host-at-your-place',
  content: <HostAtYourPlaceSection />
}, {
  key: 'explore-asks',
  content: <ExploreAsksSection />
},
// FinalCTA has its own dark inner styling
{
  key: 'final-cta',
  content: <FinalCTASectionV3 />
}, {
  key: 'banner-dont-miss-out',
  isSignInBanner: true,
  content: <SignInEmailBanner heading="Sign up now to be one of the first Sizzle Early Insiders"
  // subtext="Join Sizzle to explore what's on, connect with brands and buyers, or launch your next activation."
  buttonLabel="Sign Up" variant="dark" className="py-12" />,
  bgOverride: 'bg-gray-950'
}];
export function HomeTradeFocusedView() {
  const $ = _c(4);
  const {
    data: session
  } = useSession();
  const isLoggedIn = !!session?.user;
  let result;
  if ($[0] !== isLoggedIn) {
    result = [];
    let bgIndex = 0;
    for (const section of SECTIONS) {
      if (section.isSignInBanner && isLoggedIn) {
        continue;
      }
      if (section.bgOverride) {
        result.push({
          key: section.key,
          content: section.content,
          bg: section.bgOverride
        });
      } else {
        const bg = SECTION_BACKGROUNDS[bgIndex % SECTION_BACKGROUNDS.length];
        result.push({
          key: section.key,
          content: section.content,
          bg
        });
        bgIndex++;
      }
    }
    $[0] = isLoggedIn;
    $[1] = result;
  } else {
    result = $[1];
  }
  const visibleSections = result;
  let t0;
  if ($[2] !== visibleSections) {
    t0 = <div className="min-h-screen bg-white">{visibleSections.map(_temp)}</div>;
    $[2] = visibleSections;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function _temp(t0) {
  const {
    key,
    content,
    bg: bg_0
  } = t0;
  return <div key={key} className={bg_0}>{content}</div>;
}